// packages/core/src/learning/core-modules/build-your-plan.ts
// ─────────────────────────────────────────────────────────────────────────
// NISK CORE PATH — Module 12: "Build Your Plan" (tier: life-design)
// CAPSTONE. 5 lessons + checkpoint quiz + final test. Assembled to the real
// Lesson / ModuleAssessment / LearningModule types.
//
// applyAction types used (all in the ApplyAction union):
//   buildFreedomPlan, noToolAction, openSimulator (scenarioType 'custom'),
//   openDashboard. No Impact Lab links in this module.
// ─────────────────────────────────────────────────────────────────────────

import type {
	Lesson,
	LearningModule,
	ModuleAssessment,
} from '../LearningTypes';

const LESSON_1: Lesson = {
	id: 'build-your-plan--pick-the-goal-before-the-tool',
	title: 'Pick the goal before the tool',
	description:
		'You learn how to turn vague money improvement into a concrete goal your plan can actually track.',
	pillar: 'goal-planning',
	schoolsOfThought: ['life-design', 'personal-finance-basics', 'fi-math'],
	estimatedReadMinutes: 4,
	estimatedListenMinutes: 4,
	difficulty: 2,
	tags: ['goals', 'planning', 'life-design', 'core-path'],
	prerequisites: [],
	relatedLessons: ['build-your-plan--sequence-beats-motivation'],
	concepts: ['goal-clarity', 'money-purpose', 'plan-direction'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A money plan starts breaking when the goal is too vague. “Be better with money” sounds good, but it does not tell you what to do this month. Better plans start with a real target.',
		},
		{
			type: 'paragraph',
			text: 'The goal is not to make your life look impressive. The goal is to point your money at something that would actually change your options.',
		},
		{
			type: 'bullet_list',
			items: [
				'Move out with runway',
				'Become debt-free',
				'Build the first $10,000',
				'Create emergency runway',
				'Reach Coast FI',
				'Design a calmer lifestyle',
			],
		},
		{
			type: 'callout',
			icon: '🎯',
			text: 'A goal is useful when it changes what you do next.',
		},
		{
			type: 'example',
			label: 'Vague vs trackable',
			text: '“Save more” is vague. “Build $3,000 of runway in 10 months” is trackable. “Get ahead” is vague. “Pay off the $2,400 balance by December” gives the plan something to aim at.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is choosing a goal because it sounds adult, not because it solves the right problem. Some people chase investing when their real stress is no emergency cushion. Some chase moving out when their real problem is income. Some chase debt freedom when their real need is basic cash-flow control first.',
		},
		{
			type: 'paragraph',
			text: 'A strong goal names the life change underneath the number. Runway means less panic. Debt freedom means fewer claims on future income. First $10,000 means proof that you can build. Coast FI means reducing future pressure. A lifestyle target means building money around a life you actually want.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is picking a financial goal that sounds good but does not solve the pressure you actually feel.',
		},
		{
			type: 'key_takeaway',
			text: 'The right goal gives your money a job and your plan a direction.',
		},
	],
	audioScript:
		'A money plan needs a real goal before it needs a perfect tool. “Be better with money” is not a plan. It is a mood. It sounds responsible, but it does not tell you what happens next. A better goal points your money at a real life change. Move out with runway. Become debt-free. Build the first $10,000. Create an emergency cushion. Reach Coast FI. Build a calmer lifestyle. These are not just numbers. They are options. Runway means less panic. Debt freedom means fewer claims on future paychecks. The first $10,000 means proof that you can build. Coast FI means future pressure starts dropping. A lifestyle target means money is serving your actual life, not random comparison. The trap is picking a goal because it sounds mature. Maybe investing sounds impressive, but your real stress is no cash buffer. Maybe moving out sounds like independence, but the math says income is the first bottleneck. Maybe debt freedom matters, but cash-flow control has to come first so the debt does not come right back. A useful goal changes what you do this month. It gives the plan direction. It turns “I need to get my life together” into “This is the target, this is the timeline, and this is what the next dollar is doing.” That is where a plan begins.',
	flashcards: [
		{
			id: 'build-your-plan--pick-the-goal-before-the-tool--fc-main',
			role: 'main_idea',
			front: 'What makes a goal useful?',
			back: 'It is concrete enough to change what the user does next.',
		},
		{
			id: 'build-your-plan--pick-the-goal-before-the-tool--fc-watch',
			role: 'watch_out',
			front: 'What is the goal trap?',
			back: 'Picking a goal that sounds good but does not solve the real pressure.',
		},
		{
			id: 'build-your-plan--pick-the-goal-before-the-tool--fc-use',
			role: 'use_it',
			front: 'What should a goal name?',
			back: 'The number and the life option it creates.',
		},
	],
	applyAction: {
		type: 'buildFreedomPlan',
		label: 'Build your freedom plan',
	},
	revision: 1,
};

const LESSON_2: Lesson = {
	id: 'build-your-plan--sequence-beats-motivation',
	title: 'Sequence beats motivation',
	description:
		'You learn why the order of your goals matters more than trying to attack everything at once.',
	pillar: 'goal-planning',
	schoolsOfThought: [
		'personal-finance-basics',
		'debt-free-ramsey',
		'life-design',
	],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['planning', 'sequence', 'debt', 'runway'],
	prerequisites: ['build-your-plan--pick-the-goal-before-the-tool'],
	relatedLessons: ['build-your-plan--turn-the-goal-into-monthly-math'],
	concepts: ['goal-sequencing', 'financial-bottlenecks', 'focus-order'],
	blocks: [
		{
			type: 'paragraph',
			text: 'Most people do not fail because they have no goals. They fail because every goal is screaming at the same time. Save. Invest. Pay debt. Move out. Earn more. Build credit. Travel. Help family. Fix your life. That gets heavy fast.',
		},
		{
			type: 'paragraph',
			text: 'Sequence is what turns pressure into a path. Instead of asking, “How do I do everything?” ask, “What has to come first so the next thing becomes easier?”',
		},
		{
			type: 'bullet_list',
			items: [
				'Stop the bleeding',
				'Build basic runway',
				'Reduce high-pressure debt',
				'Grow income capacity',
				'Invest consistently',
				'Design bigger life choices',
			],
		},
		{
			type: 'callout',
			icon: '🧱',
			text: 'A plan is not a pile of goals. It is an order of operations.',
		},
		{
			type: 'example',
			label: 'Same goals, better order',
			text: 'Someone wants to move out, invest, and pay off debt. If cash flow is unstable, the first step may be creating margin. If the monthly debt payment is crushing them, debt pressure may need attention before the move. The order changes the odds.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is wanting the most exciting goal first. Moving out may feel better than building runway. Investing may feel more impressive than fixing cash flow. A big lifestyle change may feel more rewarding than boring stability. But boring stability often makes the exciting goal safer.',
		},
		{
			type: 'paragraph',
			text: 'Sequencing does not mean one perfect universal order. Different people have different bottlenecks. The point is to identify the constraint. What is actually limiting your options right now? No surplus? Too much interest pressure? No emergency buffer? Low income ceiling? Unclear spending? The first phase should reduce the biggest constraint.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is splitting energy across five goals and making none of them easier.',
		},
		{
			type: 'key_takeaway',
			text: 'The best plan is not the most ambitious pile. It is the cleanest next order.',
		},
	],
	audioScript:
		'Motivation feels good, but sequence does the real work. A lot of people have goals. That is not the issue. The issue is that every goal is yelling at the same time. Pay off debt. Save money. Invest. Move out. Build credit. Earn more. Travel. Help family. Start over. When all of that hits at once, the brain gets tired and does random things. Sequence turns the chaos into a path. The better question is not, “How do I fix everything immediately?” The better question is, “What has to happen first so the next step becomes easier?” For one person, the first move may be basic cash-flow control because money keeps leaking. For another, it may be a small runway because every emergency becomes debt. For another, it may be reducing a high-pressure payment. For another, it may be income capacity because the budget is already too tight. The order depends on the bottleneck. The emotional trap is chasing the exciting goal first. Moving out feels more powerful than building a cushion. Investing feels more impressive than fixing spending. Big life upgrades feel better than boring stability. But boring stability often makes the exciting goal safer. A plan is not a pile of goals. It is an order of operations. When the order is clean, the plan feels less like punishment and more like movement.',
	flashcards: [
		{
			id: 'build-your-plan--sequence-beats-motivation--fc-main',
			role: 'main_idea',
			front: 'Why does sequence matter?',
			back: 'The right order makes later goals easier and safer.',
		},
		{
			id: 'build-your-plan--sequence-beats-motivation--fc-watch',
			role: 'watch_out',
			front: 'What is the sequencing trap?',
			back: 'Splitting energy across too many goals at once.',
		},
		{
			id: 'build-your-plan--sequence-beats-motivation--fc-use',
			role: 'use_it',
			front: 'What should come first?',
			back: 'The step that reduces the biggest current bottleneck.',
		},
	],
	applyAction: {
		type: 'noToolAction',
		label: 'Write your goal order',
		prompt: 'List your top three money goals, then put them in the order that makes the next goal easier.',
	},
	revision: 1,
};

const LESSON_3: Lesson = {
	id: 'build-your-plan--turn-the-goal-into-monthly-math',
	title: 'Turn the goal into monthly math',
	description:
		'You learn how to translate a goal into a monthly gap between income, expenses, and the target.',
	pillar: 'goal-planning',
	schoolsOfThought: ['fi-math', 'personal-finance-basics', 'first-100k'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['monthly-plan', 'surplus', 'goals', 'savings'],
	prerequisites: ['build-your-plan--sequence-beats-motivation'],
	relatedLessons: [
		'build-your-plan--check-reality-before-you-trust-the-plan',
	],
	concepts: ['monthly-target', 'surplus-planning', 'timeline-math'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A goal becomes real when it touches the month. Annual goals sound impressive. Five-year goals sound mature. But your actual plan lives inside the next paycheck, the next bills, and the next monthly surplus.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is making a goal huge enough to feel inspiring but too blurry to act on. “Save $10,000” can feel impossible. “Find $416 per month for 24 months” is still challenging, but now it is visible.',
		},
		{
			type: 'bullet_list',
			items: [
				'Target amount',
				'Timeline',
				'Current saved amount',
				'Monthly gap',
				'Income changes',
				'Expense changes',
			],
		},
		{
			type: 'callout',
			icon: '🧮',
			text: 'If the goal does not become monthly math, it stays motivational wallpaper.',
		},
		{
			type: 'example',
			label: 'First $5,000 runway',
			text: 'If someone wants $5,000 of runway in 12 months and has $800 already saved, the remaining gap is $4,200. That means about $350 per month. Now the question becomes practical: can cash flow support that, or does income/spending need adjustment?',
		},
		{
			type: 'paragraph',
			text: 'This is where honesty matters. A plan that requires $700 per month when the real surplus is $150 is not a plan yet. It is a wish with numbers. That does not mean the goal dies. It means the timeline, income, expenses, or target needs a second pass.',
		},
		{
			type: 'paragraph',
			text: 'Monthly math also shows which lever matters most. If the gap is small, consistency may be the answer. If the gap is large, earning more may matter more than trimming tiny expenses. If the timeline is too aggressive, stretching the date may reduce pressure. If the goal is urgent, the plan may need fewer competing upgrades.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is forcing the timeline instead of adjusting the levers.',
		},
		{
			type: 'key_takeaway',
			text: 'A plan becomes usable when the target turns into a monthly number you can test.',
		},
	],
	audioScript:
		'A goal becomes real when it hits the month. Big goals sound good from far away. Save $10,000. Move out. Become debt-free. Build runway. Reach a freedom target. But the plan does not live in the slogan. It lives inside the next month. Income comes in. Bills go out. A gap remains, or it does not. That gap is where the plan happens. If someone wants $5,000 of runway in 12 months and already has $800, the remaining gap is $4,200. That is about $350 per month. Now the goal is no longer vague. It is testable. Can the current cash flow support $350 per month? If yes, the plan needs consistency. If no, the plan needs a change. Maybe the timeline stretches. Maybe expenses adjust. Maybe income becomes the real lever. Maybe the target gets staged into smaller milestones. The trap is forcing the goal to work on paper when the monthly gap does not exist in real life. That creates shame, not progress. The plan is allowed to change. The point is not to pretend the math works. The point is to see what the goal requires and then adjust the levers honestly. Monthly math is where motivation becomes a plan.',
	flashcards: [
		{
			id: 'build-your-plan--turn-the-goal-into-monthly-math--fc-main',
			role: 'main_idea',
			front: 'Where does a plan live?',
			back: 'In the monthly gap between income, expenses, and the target.',
		},
		{
			id: 'build-your-plan--turn-the-goal-into-monthly-math--fc-watch',
			role: 'watch_out',
			front: 'What is wishful planning?',
			back: 'Choosing a target that the real monthly surplus cannot support yet.',
		},
		{
			id: 'build-your-plan--turn-the-goal-into-monthly-math--fc-use',
			role: 'use_it',
			front: 'How do you test a goal?',
			back: 'Convert the target and timeline into a monthly number.',
		},
	],
	applyAction: {
		type: 'openSimulator',
		label: 'Test your monthly plan',
		scenarioType: 'custom',
	},
	revision: 1,
};

const LESSON_4: Lesson = {
	id: 'build-your-plan--check-reality-before-you-trust-the-plan',
	title: 'Check reality before you trust the plan',
	description:
		'You learn how to review your dashboard, assumptions, and actual behavior before believing your plan is on track.',
	pillar: 'goal-planning',
	schoolsOfThought: ['fi-math', 'behavioral-finance', 'risk-management'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['dashboard', 'reality-check', 'assumptions', 'planning'],
	prerequisites: ['build-your-plan--turn-the-goal-into-monthly-math'],
	relatedLessons: ['build-your-plan--build-a-plan-you-can-repeat'],
	concepts: ['plan-review', 'assumption-checking', 'behavior-feedback'],
	blocks: [
		{
			type: 'paragraph',
			text: 'A plan can look clean and still be wrong. Not because the tool is useless. Because plans are built on assumptions, and assumptions need contact with reality.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is falling in love with the version of the plan where everything goes perfectly. Perfect savings. Perfect spending. No surprise bills. No burnout. No family needs. No rent increase. No emotional spending. Real life usually has more noise.',
		},
		{
			type: 'bullet_list',
			items: [
				'Actual income',
				'Actual spending',
				'Actual surplus',
				'Debt changes',
				'Runway changes',
				'Investment assumptions',
				'Big upcoming costs',
			],
		},
		{
			type: 'callout',
			icon: '📊',
			text: 'The dashboard is not there to judge you. It is there to tell the truth sooner.',
		},
		{
			type: 'example',
			label: 'The missed surplus',
			text: 'A plan assumes $500 per month of surplus, but the last three months show closer to $180. That is not failure data. It is correction data. The plan may need a slower timeline, an expense change, or an income lever.',
		},
		{
			type: 'paragraph',
			text: 'Reality checks protect you from fake confidence. If the plan assumes a raise, mark it as an assumption. If it assumes spending cuts, check whether those cuts actually happened. If it assumes no major purchase, look at the next 90 days. If it assumes consistent investing, be honest about volatility and uncertainty.',
		},
		{
			type: 'paragraph',
			text: 'This is also how you avoid shame. Shame says, “I failed.” Feedback says, “This input was wrong.” That difference matters. A plan is not a personality test. It is a working model that improves when the numbers get more honest.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is treating an optimistic plan like proof instead of a draft.',
		},
		{
			type: 'key_takeaway',
			text: 'Trust the plan more after reality has had a chance to challenge it.',
		},
	],
	audioScript:
		'A plan can look clean and still be wrong. That does not mean planning is pointless. It means a plan is only as good as the assumptions underneath it. Maybe the plan assumes you can save $500 per month, but the last three months show $180. Maybe it assumes no big expenses, but your car, move, family obligation, or medical bill is coming. Maybe it assumes a raise that has not happened yet. Maybe it assumes you will suddenly stop stress spending without changing the situation that creates the stress. This is why reality checks matter. The dashboard is not there to judge you. It is there to tell the truth sooner. If the numbers are off, that is not failure data. It is correction data. The timeline may need to slow down. The income lever may matter more. The spending goal may need to become more realistic. The target may need stages. The point is not to shame yourself into consistency. Shame usually makes people avoid the plan. Feedback makes the plan better. Look at actual income, actual spending, actual surplus, debt changes, runway, assumptions, and upcoming costs. The plan gets stronger when it stops being a fantasy version of you and starts working with the real version. Trust the plan after reality has challenged it.',
	flashcards: [
		{
			id: 'build-your-plan--check-reality-before-you-trust-the-plan--fc-main',
			role: 'main_idea',
			front: 'Why check reality?',
			back: 'Plans depend on assumptions, and assumptions need to be tested.',
		},
		{
			id: 'build-your-plan--check-reality-before-you-trust-the-plan--fc-watch',
			role: 'watch_out',
			front: 'What is false confidence?',
			back: 'Trusting an optimistic plan before actual behavior supports it.',
		},
		{
			id: 'build-your-plan--check-reality-before-you-trust-the-plan--fc-use',
			role: 'use_it',
			front: 'What should you review?',
			back: 'Actual income, spending, surplus, debt, runway, assumptions, and upcoming costs.',
		},
	],
	applyAction: {
		type: 'openDashboard',
		label: 'Check your dashboard',
	},
	revision: 1,
};

const LESSON_5: Lesson = {
	id: 'build-your-plan--build-a-plan-you-can-repeat',
	title: 'Build a plan you can repeat',
	description:
		'You learn how to make your plan durable enough to survive normal life instead of depending on perfect motivation.',
	pillar: 'goal-planning',
	schoolsOfThought: ['life-design', 'behavioral-finance', 'rich-life-ramit'],
	estimatedReadMinutes: 5,
	estimatedListenMinutes: 5,
	difficulty: 3,
	tags: ['systems', 'consistency', 'planning', 'habits'],
	prerequisites: ['build-your-plan--check-reality-before-you-trust-the-plan'],
	relatedLessons: ['build-your-plan--pick-the-goal-before-the-tool'],
	concepts: ['repeatable-system', 'plan-durability', 'motivation-proofing'],
	blocks: [
		{
			type: 'paragraph',
			text: 'The final plan is not the one that looks most intense. It is the one you can repeat when life gets normal again. Motivation spikes. Real life returns. Bills hit. Work gets stressful. Friends invite you out. Family needs something. The plan has to survive that version of you.',
		},
		{
			type: 'paragraph',
			text: 'The emotional trap is building a plan for your best day only. Best-day you wants to save aggressively, cook every meal, avoid every impulse, work extra, invest perfectly, and never get tired. Real you needs a system.',
		},
		{
			type: 'bullet_list',
			items: [
				'Clear goal',
				'Clean sequence',
				'Monthly target',
				'Reality review',
				'Planned fun',
				'Adjustment rules',
			],
		},
		{
			type: 'callout',
			icon: '🔁',
			text: 'A repeatable plan beats a heroic plan.',
		},
		{
			type: 'example',
			label: 'The durable version',
			text: 'A heroic plan says, “I will save $900 every month no matter what.” A durable plan says, “The target is $600, the stretch is $800, and if a surprise bill hits, I protect the habit and adjust the timeline.” That plan has a better chance of surviving.',
		},
		{
			type: 'paragraph',
			text: 'A repeatable plan has room for life. It includes some enjoyment, because a plan that makes the present feel punished usually breaks. It includes review dates, because numbers change. It includes tradeoff rules, because not every month will be clean.',
		},
		{
			type: 'paragraph',
			text: 'This is the real Core Path finish line. Not becoming perfect. Not knowing every finance term. Not turning money into your whole personality. The win is having a working system: know your numbers, control cash flow, respect debt, build safety, invest with humility, understand your behavior, test big decisions, and keep updating the plan.',
		},
		{
			type: 'callout',
			icon: '⚠️',
			text: 'The trap is mistaking intensity for maturity.',
		},
		{
			type: 'key_takeaway',
			text: 'Your plan is successful when it keeps working after motivation fades.',
		},
	],
	audioScript:
		'The best plan is not the most intense one. It is the one you can repeat. That matters because motivation lies. Not maliciously, but it overpromises. Best-day you thinks you can save aggressively, cook every meal, never impulse spend, work extra hours, fix every habit, and execute perfectly forever. Real you has bills, stress, friends, family, tired weeks, surprise costs, and emotions. So the plan has to be built for real you. A repeatable plan has a clear goal, a clean sequence, monthly math, reality checks, some room for enjoyment, and rules for adjusting when life gets messy. That is not weakness. That is durability. A heroic plan says, “I will save $900 every month no matter what.” A durable plan says, “The target is $600, the stretch is $800, and if something hits, the habit stays alive while the timeline adjusts.” That second plan is less dramatic, but stronger. This is the real finish line of the Core Path. Not becoming perfect. Not memorizing finance words. Not making money your whole personality. The win is building a working system. You know your numbers. You control cash flow. You respect debt. You build safety. You invest with humility. You understand behavior. You test big decisions. You keep updating the plan. That is how money starts buying options in real life.',
	flashcards: [
		{
			id: 'build-your-plan--build-a-plan-you-can-repeat--fc-main',
			role: 'main_idea',
			front: 'What makes a plan strong?',
			back: 'It is repeatable after motivation fades.',
		},
		{
			id: 'build-your-plan--build-a-plan-you-can-repeat--fc-watch',
			role: 'watch_out',
			front: 'What is the intensity trap?',
			back: 'Building a heroic plan for best-day you instead of real-life you.',
		},
		{
			id: 'build-your-plan--build-a-plan-you-can-repeat--fc-use',
			role: 'use_it',
			front: 'What should a durable plan include?',
			back: 'A goal, sequence, monthly target, review rhythm, enjoyment, and adjustment rules.',
		},
	],
	applyAction: {
		type: 'buildFreedomPlan',
		label: 'Finalize your plan',
	},
	revision: 1,
};

const ASSESSMENT: ModuleAssessment = {
	moduleId: 'build-your-plan',
	checkpointQuiz: [
		{
			type: 'multiple_choice',
			id: 'build-your-plan--cq--q1',
			question: 'What makes a financial goal useful?',
			options: [
				'It sounds impressive',
				'It is concrete enough to change what happens next',
				'It copies someone else’s life',
				'It avoids numbers',
			],
			correctIndex: 1,
			explanation:
				'A useful goal gives the plan direction and changes the next action.',
		},
		{
			type: 'true_false',
			id: 'build-your-plan--cq--q2',
			question:
				'“Be better with money” is usually too vague to guide a monthly plan.',
			correct: true,
			explanation:
				'It does not define the target, timeline, or next step.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--cq--q3',
			scenario:
				'Someone says their goal is to “get ahead,” but they cannot tell whether that means debt payoff, runway, investing, or moving out.',
			question: 'What is missing?',
			options: [
				'Goal clarity',
				'More motivation quotes',
				'A more expensive tool',
				'A perfect market forecast',
			],
			correctIndex: 0,
			explanation:
				'The person needs to name the specific life option they are trying to create.',
		},
		{
			type: 'multiple_choice',
			id: 'build-your-plan--cq--q4',
			question: 'Why does sequencing matter?',
			options: [
				'It makes every goal easy',
				'It puts goals in an order where one step makes the next easier',
				'It removes all tradeoffs',
				'It replaces income',
			],
			correctIndex: 1,
			explanation:
				'Sequence turns a pile of goals into an order of operations.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--cq--q5',
			scenario:
				'Maya wants to move out, invest, and pay off debt, but her cash flow is unstable and she has no buffer.',
			question: 'What does Module 12 suggest she identify first?',
			options: [
				'The most exciting goal',
				'The biggest current bottleneck',
				'The goal that sounds best online',
				'The fastest-looking shortcut',
			],
			correctIndex: 1,
			explanation:
				'The first phase should reduce the constraint that is limiting the rest of the plan.',
		},
		{
			type: 'true_false',
			id: 'build-your-plan--cq--q6',
			question:
				'Trying to attack five major goals at once can make progress weaker.',
			correct: true,
			explanation:
				'Splitting energy across too many goals can prevent any one goal from becoming easier.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--cq--q7',
			scenario:
				'Someone wants $5,000 saved in 12 months and already has $800.',
			question: 'What monthly number roughly tests the plan?',
			options: ['$50', '$150', '$350', '$900'],
			correctIndex: 2,
			explanation:
				'$5,000 minus $800 leaves $4,200. Over 12 months, that is about $350 per month.',
		},
	],
	finalTest: [
		{
			type: 'multiple_choice',
			id: 'build-your-plan--final--q1',
			question: 'What is the main purpose of Module 12?',
			options: [
				'To memorize finance terms',
				'To turn learning into a concrete, reviewable plan',
				'To predict the future perfectly',
				'To choose one universal financial path',
			],
			correctIndex: 1,
			explanation:
				'Module 12 helps users convert the Core Path into a practical plan they can test and update.',
		},
		{
			type: 'true_false',
			id: 'build-your-plan--final--q2',
			question:
				'A money goal should connect a number to a real-life option.',
			correct: true,
			explanation:
				'Strong goals name both the target and what the target changes in the user’s life.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--final--q3',
			scenario:
				'Ben chooses “invest more” as his first goal, but his actual stress is that one surprise bill sends him back into debt.',
			question: 'What is the likely issue?',
			options: [
				'The goal may not match the real pressure',
				'Investing is always wrong',
				'Plans should ignore stress',
				'He needs more goals at once',
			],
			correctIndex: 0,
			explanation:
				'Module 12 teaches users to choose a goal that solves the actual bottleneck or pressure.',
		},
		{
			type: 'multiple_choice',
			id: 'build-your-plan--final--q4',
			question: 'What is a plan sequence?',
			options: [
				'A random list of wishes',
				'The order of goals based on what makes the next step easier',
				'A promise that life will go perfectly',
				'A way to avoid tradeoffs',
			],
			correctIndex: 1,
			explanation: 'Sequence is the order of operations for the plan.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--final--q5',
			scenario:
				'Someone has a $10,000 goal, a 20-month timeline, and $0 saved.',
			question: 'What monthly amount does the plan need to test?',
			options: ['$100', '$250', '$500', '$1,500'],
			correctIndex: 2,
			explanation: '$10,000 over 20 months equals $500 per month.',
		},
		{
			type: 'true_false',
			id: 'build-your-plan--final--q6',
			question:
				'If the monthly math does not work, the goal is automatically dead.',
			correct: false,
			explanation:
				'The target, timeline, income, expenses, or sequence can be adjusted.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--final--q7',
			scenario:
				'A plan assumes $600 monthly surplus, but the dashboard shows the user has averaged $220 for the last three months.',
			question: 'What is the best interpretation?',
			options: [
				'The user is a failure',
				'The plan needs correction data from reality',
				'The dashboard should be ignored',
				'The goal is guaranteed anyway',
			],
			correctIndex: 1,
			explanation:
				'Actual behavior gives feedback so the plan can become more honest.',
		},
		{
			type: 'multiple_choice',
			id: 'build-your-plan--final--q8',
			question: 'Why should assumptions be reviewed?',
			options: [
				'Because plans are built on assumptions that can be wrong',
				'Because every assumption is useless',
				'Because reviewing guarantees success',
				'Because numbers do not matter',
			],
			correctIndex: 0,
			explanation:
				'Income, spending, returns, expenses, and life events can all differ from the plan.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--final--q9',
			scenario:
				'A person builds a plan that works only if they never eat out, never get tired, never face surprise bills, and never spend emotionally.',
			question: 'What is the problem?',
			options: [
				'The plan is built for best-day fantasy',
				'The plan is too realistic',
				'The person has too much flexibility',
				'The plan has too much room for life',
			],
			correctIndex: 0,
			explanation:
				'Durable plans are built for real life, not a perfect version of the user.',
		},
		{
			type: 'true_false',
			id: 'build-your-plan--final--q10',
			question:
				'Planned enjoyment can make a financial plan more durable.',
			correct: true,
			explanation:
				'A plan that makes present life feel punished often breaks.',
		},
		{
			type: 'multiple_choice',
			id: 'build-your-plan--final--q11',
			question: 'What is the final Core Path win?',
			options: [
				'Never changing the plan',
				'Knowing every finance term',
				'Having a working system that can be reviewed and adjusted',
				'Ignoring emotions forever',
			],
			correctIndex: 2,
			explanation:
				'The Core Path ends with a repeatable system, not perfection.',
		},
		{
			type: 'scenario',
			id: 'build-your-plan--final--q12',
			scenario:
				'A user has a goal, a sequence, monthly math, review dates, and rules for adjusting when life gets messy.',
			question: 'What kind of plan is this?',
			options: [
				'A heroic plan',
				'A repeatable plan',
				'A fantasy plan',
				'A guaranteed plan',
			],
			correctIndex: 1,
			explanation:
				'Repeatable plans are built to survive normal life and changing conditions.',
		},
	],
};

export const BUILD_YOUR_PLAN_MODULE: LearningModule = {
	id: 'build-your-plan',
	title: 'Build Your Plan',
	description:
		'The capstone: turn everything you have learned into a working plan. Pick a goal that solves real pressure, sequence it around your biggest bottleneck, convert it to monthly math, check it against reality, and build a version durable enough to survive normal life.',
	emoji: '🗺️',
	accentColor: '#c8ff00',
	tier: 'life-design',
	pillar: 'goal-planning',
	order: 12,
	lessons: [LESSON_1, LESSON_2, LESSON_3, LESSON_4, LESSON_5],
	assessment: ASSESSMENT,
};
